import React from 'react';
import { KpiCard, Stat, Progress } from '../ui';
import { formatMoneyCompact, formatPercent } from '../../lib/format';

/**
 * The 4-card KPI row at the top of the Projects Dashboard. Replaces the
 * legacy `stats-grid` (Total / Active / Contract / Left to bill / Billed
 * — 5 cards in 4+1 ragged rows) with a balanced 4-up consolidation that
 * groups related metrics inside cards instead of splitting them across.
 *
 * Layout:
 *   [ Total projects ]  [ Contract value ]  [ Total billed* ]  [ Left to bill† ]
 *
 *   *Total billed includes a dashed-divider sub-stat block for Retainage.
 *   †Left to bill includes a progress bar showing billed-of-contract.
 *
 * Q-to-date / YTD deltas (e.g. "+2 QTD", "+$2.16M YTD") are intentionally
 * omitted in v1 — the underlying data isn't aggregated server-side yet.
 * They can be added later by passing optional `*Delta` props.
 *
 * Props are pre-computed totals. The parent (ProjectList) does the
 * reduce()s and passes the scalars in — keeping this component thin
 * and easy to reuse on other dashboards (PM view, client view, etc.).
 */
export default function DashboardKpiRow({
  totalProjects,
  activeCount,
  archivedCount,
  totalContractValue,
  totalBilled,
  totalRetainage,
  leftToBill,
}) {
  const billedPct = totalContractValue > 0
    ? (totalBilled / totalContractValue) * 100
    : 0;
  const remainingPct = totalContractValue > 0
    ? (leftToBill / totalContractValue) * 100
    : 0;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 12,
        marginBottom: 18,
      }}
    >
      {/* 1. Total projects */}
      <KpiCard>
        <Stat
          label="Total projects"
          value={String(totalProjects)}
          sub={`${activeCount} active · ${archivedCount} archived`}
        />
      </KpiCard>

      {/* 2. Contract value */}
      <KpiCard>
        <Stat
          label="Contract value"
          value={formatMoneyCompact(totalContractValue)}
          sub={`across ${totalProjects} project${totalProjects === 1 ? '' : 's'}`}
        />
      </KpiCard>

      {/* 3. Total billed — with nested Retainage sub-stat below dashed
            divider, per the handoff spec. */}
      <KpiCard>
        <Stat
          label="Total billed"
          value={formatMoneyCompact(totalBilled)}
          delta={`${formatPercent(billedPct)} of contract`}
          deltaTone="neut"
        />
        {totalRetainage > 0 && (
          <div
            style={{
              borderTop: '1px dashed var(--border-default)',
              paddingTop: 8,
              marginTop: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
                Retainage
              </span>
              <span
                className="mono"
                style={{
                  fontSize: 13.5,
                  color: 'var(--warning)',
                  fontWeight: 500,
                }}
              >
                {formatMoneyCompact(totalRetainage)}
              </span>
            </div>
          </div>
        )}
      </KpiCard>

      {/* 4. Left to bill — with progress bar showing billed-of-contract. */}
      <KpiCard>
        <Stat
          label="Left to bill"
          value={formatMoneyCompact(leftToBill)}
          delta={`${formatPercent(remainingPct)} remaining`}
          deltaTone="neut"
        />
        <div
          style={{
            marginTop: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <Progress value={billedPct} height={5} />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12.5,
              color: 'var(--text-3)',
            }}
          >
            <span>Billed</span>
            <span className="mono">{formatPercent(billedPct)}</span>
          </div>
        </div>
      </KpiCard>
    </div>
  );
}
